import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api/client.js";

export function useGeofences() {
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);

  const loadGeofences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGeofences();
      const validData = Array.isArray(data) ? data : [];
      setGeofences(validData);
    } catch (err) {
      setError(err.message);
      console.error("Failed to load geofences:", err);
      setGeofences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGeofences();
  }, [loadGeofences]);

  const createGeofence = useCallback(async (body) => {
    try {
      const newGf = await api.createGeofence(body);
      if (!newGf || typeof newGf !== "object") {
        throw new Error("Invalid response from server");
      }
      setGeofences((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return [newGf, ...prevArray];
      });
      return newGf;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateGeofence = useCallback(async (id, body) => {
    try {
      const updated = await api.updateGeofence(id, body);
      setGeofences((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.map((gf) => (gf.id === id ? updated : gf));
      });
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteGeofence = useCallback(async (id) => {
    try {
      await api.deleteGeofence(id);
      setGeofences((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return prevArray.filter((gf) => gf.id !== id);
      });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateAssignments = useCallback(async (id, imeis) => {
    try {
      const assignments = await api.updateGeofenceAssignments(id, imeis);
      return assignments;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      setPresetsLoading(true);
      const data = await api.getGeofencePresets();
      setPresets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setPresets([]);
    } finally {
      setPresetsLoading(false);
    }
  }, []);

  const importPreset = useCallback(async (presetId, imeis = []) => {
    try {
      const forked = await api.importGeofencePreset(presetId, { imeis });
      setGeofences((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return [forked, ...prevArray];
      });
      return forked;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Identidad estable del objeto retornado: se pasa entero como prop a páginas
  // memoizadas (MonitorPage, GeocercasPage) — un literal nuevo por render las
  // re-renderizaría en cada mensaje WS aunque nada de geocercas cambie.
  return useMemo(
    () => ({
      geofences,
      loading,
      error,
      loadGeofences,
      createGeofence,
      updateGeofence,
      deleteGeofence,
      updateAssignments,
      presets,
      presetsLoading,
      loadPresets,
      importPreset,
    }),
    [
      geofences,
      loading,
      error,
      loadGeofences,
      createGeofence,
      updateGeofence,
      deleteGeofence,
      updateAssignments,
      presets,
      presetsLoading,
      loadPresets,
      importPreset,
    ],
  );
}
